import { useState, useRef, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useCanvasStore } from './canvas-store';
import { useActiveProjectImages } from './store-selectors';

const AD_STYLES = [
  'Problem-Solution',
  'Benefits',
  'Features',
  'Reviews',
  'Listicle',
  'Advertorial',
  'Us vs Them',
  'Education',
  'UGC Style',
  'Before & After',
  'Founder Story',
  'Social Proof',
  'Urgency',
  'Product Launch',
  'Sale',
  'How It Works',
  'Myth Busting',
  'Dark Mode',
];

interface StyleVariationsPanelProps {
  selectedImageId: string;
  onClose: () => void;
}

interface StyleProgress {
  style: string;
  index: number;
  total: number;
  status: 'running' | 'done' | 'failed';
}

export function StyleVariationsPanel({ selectedImageId, onClose }: StyleVariationsPanelProps) {
  const [selectedStyles, setSelectedStyles] = useState<Set<string>>(new Set());
  const [qty, setQty] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<StyleProgress[]>([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const { addImageNextTo, addImageToCanvas, addCanvasPlaceholder, resolveCanvasPlaceholder, failCanvasPlaceholder, saveConversationHistory } = useCanvasStore();
  const activeBrandId = useCanvasStore((s) => s.activeBrandId);
  const activeProjectId = useCanvasStore((s) => s.activeProjectId);
  const images = useActiveProjectImages();
  const selectedImage = images.find((img) => img.id === selectedImageId);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!panelRef.current) return;
      // Click inside the panel itself? Stay open.
      if (panelRef.current.contains(target)) return;
      // Click anywhere inside the toolbar? Let the toolbar's button
      // handlers manage state (prevents the mousedown→click toggle race
      // that made the panel feel stuck open when re-clicking the trigger).
      const toolbarEl = document.querySelector('[data-testid="canvas-toolbar"]');
      if (toolbarEl && toolbarEl.contains(target)) return;
      // Generation runs in the background — let the user dismiss the
      // panel anytime, even mid-generation.
      onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const toggleStyle = (style: string) => {
    setSelectedStyles((prev) => {
      const next = new Set(prev);
      if (next.has(style)) next.delete(style);
      else next.add(style);
      return next;
    });
  };

  const totalToGenerate = selectedStyles.size * qty;

  const handleGenerate = async () => {
    if (!selectedImage || selectedStyles.size === 0) return;
    setIsGenerating(true);
    setCompletedCount(0);

    const stylesArray: string[] = [];
    Array.from(selectedStyles).forEach((style) => {
      for (let i = 0; i < qty; i++) {
        stylesArray.push(style);
      }
    });
    setTotalCount(stylesArray.length);
    setProgress([]);

    console.log('[StyleVariationsPanel] generating styles:', stylesArray, 'for image:', selectedImage.src.slice(0, 60));

    // Mirror the run into the chat so it appears in conversation history
    // like campaign-v2 / V3 variations do. Pinned to the project at click
    // time so subsequent project switches don't dump messages elsewhere.
    const pinnedProjectId = activeProjectId;
    const userMessageId = `msg-user-stylevar-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const assistantMessageId = `msg-ai-stylevar-${Date.now() + 1}-${Math.random().toString(36).slice(2)}`;
    const styleSummary = Array.from(selectedStyles)
      .map((s) => qty > 1 ? `${s} ×${qty}` : s)
      .join(', ');
    let qaPassCount = 0;
    let completedCountLocal = 0;

    // Pre-compute placeholder positions on a new row below ALL existing
    // canvas content — both real images AND in-flight placeholders. If a
    // prior generation is still in progress (its placeholders visible but
    // not yet resolved), the new batch must land BELOW those too, not on
    // top of them. Previously this only considered proj.images and ignored
    // proj.canvasPlaceholders, so back-to-back clicks stacked batches.
    const GEN_GAP = 24;
    const placeholderDisplayScale = Math.min(320 / (selectedImage.originalWidth || 1080), 320 / (selectedImage.originalHeight || 1080), 1);
    const placeholderDisplayW = Math.round((selectedImage.originalWidth || 1080) * placeholderDisplayScale);
    const placeholderDisplayH = Math.round((selectedImage.originalHeight || 1080) * placeholderDisplayScale);
    let placeholderTargetY = selectedImage.y + selectedImage.displayHeight + GEN_GAP;
    if (pinnedProjectId) {
      const proj = useCanvasStore.getState().projects[pinnedProjectId];
      if (proj) {
        const imageBottom = proj.images.reduce(
          (max, im) => Math.max(max, im.y + im.displayHeight),
          selectedImage.y + selectedImage.displayHeight,
        );
        const placeholderBottom = Object.values(proj.canvasPlaceholders).reduce(
          (max, ph) => Math.max(max, ph.y + ph.height),
          0,
        );
        placeholderTargetY = Math.max(imageBottom, placeholderBottom) + GEN_GAP;
      }
    }
    const placeholderTaskIds: string[] = stylesArray.map((_, idx) => `stylevar-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`);
    const placeholderPositions: Array<{ x: number; y: number }> = stylesArray.map((_, idx) => ({
      x: selectedImage.x + idx * (placeholderDisplayW + GEN_GAP),
      y: placeholderTargetY,
    }));
    // Drop the placeholders now so the user sees grey loading boxes during
    // generation — same UX as campaign-v2 ad_generation.
    stylesArray.forEach((style, idx) => {
      addCanvasPlaceholder(
        placeholderTaskIds[idx],
        placeholderPositions[idx].x,
        placeholderPositions[idx].y,
        placeholderDisplayW,
        placeholderDisplayH,
        `${style} variation of source ad`,
        selectedImage.originalWidth || 1080,
        selectedImage.originalHeight || 1080,
        `style_${style}_pending_${idx}.jpg`,
        pinnedProjectId ?? undefined,
      );
    });
    if (pinnedProjectId) {
      const now = Date.now();
      useCanvasStore.getState()._writeMessages(pinnedProjectId, (msgs) => [
        ...msgs,
        {
          id: userMessageId,
          role: 'user' as const,
          content: `Generate variations: ${styleSummary}`,
          thumbnails: [selectedImage.src],
          timestamp: now,
        },
        {
          id: assistantMessageId,
          role: 'assistant' as const,
          content: `On it — generating ${stylesArray.length} style variation${stylesArray.length === 1 ? '' : 's'} now.`,
          isStreaming: true,
          thumbnails: [],
          timestamp: now + 1,
        },
      ]);
      // Persist immediately so user message survives if the user navigates
      // away before the variations complete.
      saveConversationHistory(pinnedProjectId).catch(() => {});
    }

    try {
      const response = await fetch('/api/canvas/style-variations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: selectedImage.src,
          styles: stylesArray,
          width: selectedImage.originalWidth || 1080,
          height: selectedImage.originalHeight || 1080,
          brandId: activeBrandId ?? undefined,
        }),
      });

      if (!response.ok || !response.body) throw new Error('Request failed');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const blocks = sseBuffer.split('\n\n');
        sseBuffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;
          const eventMatch = block.match(/^event: (.+)$/m);
          const dataMatch = block.match(/^data: (.+)$/m);
          if (!eventMatch || !dataMatch) continue;
          const eventType = eventMatch[1].trim();
          let data: Record<string, unknown>;
          try { data = JSON.parse(dataMatch[1]); } catch { continue; }

          if (eventType === 'style_progress') {
            setProgress((prev) => {
              const idx = prev.findIndex((p) => p.index === (data.index as number));
              const entry: StyleProgress & { attempt?: number } = {
                style: data.style as string,
                index: data.index as number,
                total: data.total as number,
                status: data.status as 'running' | 'done' | 'failed',
                attempt: typeof data.attempt === 'number' ? (data.attempt as number) : undefined,
              };
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = entry;
                return next;
              }
              return [...prev, entry];
            });
          }

          if (eventType === 'style_complete') {
            const imgUrl = data.imageUrl as string;
            const imgW = (data.width as number) || selectedImage.originalWidth || 1080;
            const imgH = (data.height as number) || selectedImage.originalHeight || 1080;
            const variationIdx = (typeof data.index === 'number') ? (data.index as number) : completedCountLocal;
            const filename = `style_${data.style}_${Date.now()}.jpg`;
            const styleVal = data.style as string;
            const adMeta = {
              adStyle: styleVal,
              conceptName: `${styleVal} variation`,
            };
            // Resolve the placeholder for THIS variation (fades out 400ms later).
            const taskId = placeholderTaskIds[variationIdx];
            if (taskId) resolveCanvasPlaceholder(taskId, pinnedProjectId ?? undefined);
            // Place the real image at the same coords the placeholder used,
            // so the swap is visually seamless. addImageToCanvas also POSTs
            // to the server immediately — the canvas_image row exists by the
            // time saveImagesSnapshot fires, so QA persistence is deterministic.
            const pos = placeholderPositions[variationIdx];
            addImageToCanvas(
              imgUrl, imgW, imgH, filename,
              imgW, imgH, `${styleVal} style variation`,
              pos?.x, pos?.y, adMeta,
              pinnedProjectId ?? undefined,
            );
            // Mirror progress into the chat: append thumbnail + update count.
            completedCountLocal++;
            const thisQa = data.qaPassed as boolean | null | undefined;
            if (thisQa === true) qaPassCount++;
            if (pinnedProjectId) {
              const completedNow = completedCountLocal;
              const passedNow = qaPassCount;
              useCanvasStore.getState()._writeMessages(pinnedProjectId, (msgs) =>
                msgs.map((m) =>
                  m.id === assistantMessageId
                    ? {
                        ...m,
                        thumbnails: [...(m.thumbnails || []), imgUrl],
                        content: completedNow < stylesArray.length
                          ? `Generating variation ${completedNow + 1} of ${stylesArray.length}…  (QA so far: ${passedNow}/${completedNow})`
                          : `Done — ${stylesArray.length} variation${stylesArray.length === 1 ? '' : 's'} generated. QA: ${passedNow}/${stylesArray.length} passed.`,
                      }
                    : m
                )
              );
            }
            // Drop the QA badge into the project's imageQaBadges map keyed
            // by image URL (same shape campaign-v2 uses). Also PATCH the
            // canvas_image row once the client_id is known so the badge
            // survives a reload.
            const qaPassed = data.qaPassed as boolean | null | undefined;
            const qaReason = data.qaReason as string | null | undefined;
            if (activeProjectId && (qaPassed !== undefined && qaPassed !== null)) {
              const passed = !!qaPassed;
              const reasonStr = qaReason || (passed ? 'QA Passed' : 'QA Failed');
              useCanvasStore.getState()._writeQaBadges(activeProjectId, (badges) => ({
                ...badges,
                [imgUrl]: { passed, score: 0, badgeType: passed ? 'pass' : 'fail', reason: reasonStr, attempt: 0 },
              }));
              (async () => {
                for (let attempt = 0; attempt < 12; attempt++) {
                  await new Promise((r) => setTimeout(r, 250));
                  const projState = useCanvasStore.getState().projects[activeProjectId];
                  const found = projState?.images?.find((im) => im.src === imgUrl);
                  if (found?.id) {
                    try {
                      await fetch(`/api/canvas/projects/${activeProjectId}/images/${found.id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ qaPassed: passed, qaReason: reasonStr }),
                      });
                    } catch (e) {
                      console.warn('[StyleVariationsPanel] qa PATCH failed:', e);
                    }
                    break;
                  }
                }
              })();
            }
            setCompletedCount((c) => c + 1);
          }

          if (eventType === 'done') {
            // Stop streaming on the synthetic assistant message + persist
            // the final state (thumbnails + content) so it survives reload.
            if (pinnedProjectId) {
              useCanvasStore.getState()._writeMessages(pinnedProjectId, (msgs) =>
                msgs.map((m) => m.id === assistantMessageId ? { ...m, isStreaming: false } : m)
              );
              saveConversationHistory(pinnedProjectId).catch(() => {});
            }
            // Auto-fit the canvas viewport to show the source + all just-placed
            // variations. Without this the user can't see where they landed,
            // especially when the source is far from the current viewport.
            if (activeProjectId && selectedImage) {
              const proj = useCanvasStore.getState().projects[activeProjectId];
              if (proj) {
                const sourceY = selectedImage.y;
                const sourceX = selectedImage.x;
                const rowImages = proj.images.filter((im) =>
                  im.y >= sourceY && im.y <= sourceY + selectedImage.displayHeight + 40 &&
                  im.x >= sourceX - 80
                );
                if (rowImages.length > 0) {
                  const minX = Math.min(...rowImages.map((im) => im.x));
                  const maxX = Math.max(...rowImages.map((im) => im.x + im.displayWidth));
                  const minY = Math.min(...rowImages.map((im) => im.y));
                  const maxY = Math.max(...rowImages.map((im) => im.y + im.displayHeight));
                  useCanvasStore.getState()._writeProjectView(activeProjectId, {
                    pendingFitToRow: { minX, minY, maxX, maxY },
                    isCanvasOpen: true,
                  });
                }
              }
            }
            break;
          }
        }
      }
    } catch (err) {
      console.error('[StyleVariationsPanel] Error:', err);
      // Clean up any placeholders that never resolved so they don't
      // sit on the canvas forever.
      placeholderTaskIds.forEach((tid) => failCanvasPlaceholder(tid, 'Generation failed', pinnedProjectId ?? undefined));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      ref={panelRef}
      data-testid="style-variations-panel"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 12px)',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 420,
        background: '#FFFFFF',
        border: '1px solid #E8E5E0',
        borderRadius: 14,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        zIndex: 300,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 12px' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1A1A1A' }}>Create Style Variations</div>
          <div style={{ fontSize: 11, color: '#8C8578', marginTop: 2 }}>Select styles to generate. One ad per style per quantity.</div>
        </div>
        <button
          data-testid="style-variations-close"
          onClick={onClose}
          disabled={isGenerating}
          style={{ background: 'none', border: 'none', cursor: isGenerating ? 'not-allowed' : 'pointer', color: '#8C8578', padding: 4, borderRadius: 6 }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Style pills */}
      <div style={{ padding: '0 16px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {AD_STYLES.map((style) => {
          const isSelected = selectedStyles.has(style);
          return (
            <button
              key={style}
              data-testid={`style-pill-${style.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => !isGenerating && toggleStyle(style)}
              disabled={isGenerating}
              style={{
                padding: '5px 12px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 500,
                border: isSelected ? '1px solid #818CF8' : '1px solid rgba(255,255,255,0.12)',
                background: isSelected ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.04)',
                color: isSelected ? '#6366F1' : '#9CA3AF',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {style}
            </button>
          );
        })}
      </div>

      {/* Qty stepper */}
      <div style={{ padding: '10px 16px', borderTop: '1px solid #F0EDE8', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: '#8C8578', flex: 1 }}>Qty per style:</span>
        <button
          data-testid="style-qty-minus"
          onClick={() => setQty((q) => Math.max(1, q - 1))}
          disabled={isGenerating || qty <= 1}
          style={{ width: 28, height: 28, borderRadius: 6, background: '#F5F3EE', border: '1px solid #E5E2DC', color: '#3D3A36', cursor: qty <= 1 || isGenerating ? 'not-allowed' : 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: qty <= 1 || isGenerating ? 0.4 : 1 }}
        >
          −
        </button>
        <span style={{ width: 24, textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{qty}</span>
        <button
          data-testid="style-qty-plus"
          onClick={() => setQty((q) => Math.min(4, q + 1))}
          disabled={isGenerating || qty >= 4}
          style={{ width: 28, height: 28, borderRadius: 6, background: '#F5F3EE', border: '1px solid #E5E2DC', color: '#3D3A36', cursor: qty >= 4 || isGenerating ? 'not-allowed' : 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: qty >= 4 || isGenerating ? 0.4 : 1 }}
        >
          +
        </button>
      </div>

      {/* Progress */}
      {isGenerating && (() => {
        // If any variation is currently on attempt 2+, surface that so the
        // user knows it's retrying rather than thinking the modal froze.
        const retrying = progress.filter((p) => p.status === 'running' && (p as { attempt?: number }).attempt && (p as { attempt?: number }).attempt! > 1);
        return (
          <div style={{ padding: '10px 16px', borderTop: '1px solid #F0EDE8', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Loader2 size={14} style={{ color: '#6366F1', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 12, color: '#6366F1' }}>
              Generating {completedCount} / {totalCount}
              {retrying.length > 0 ? `  ·  retrying ${retrying.length} (QA fail)` : ''}…
            </span>
          </div>
        );
      })()}

      {/* Generate button */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #F0EDE8' }}>
        <button
          data-testid="style-generate-btn"
          onClick={handleGenerate}
          disabled={isGenerating || selectedStyles.size === 0 || !selectedImage}
          style={{
            width: '100%',
            padding: '11px 0',
            borderRadius: 9,
            background: selectedStyles.size === 0 || !selectedImage || isGenerating ? 'rgba(99,102,241,0.3)' : '#6366F1',
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: 14,
            border: 'none',
            cursor: selectedStyles.size === 0 || !selectedImage || isGenerating ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            opacity: selectedStyles.size === 0 || !selectedImage ? 0.5 : 1,
          }}
        >
          {isGenerating
            ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
            : `Generate ${totalToGenerate > 0 ? totalToGenerate : ''} Style Variation${totalToGenerate !== 1 ? 's' : ''}`
          }
        </button>
      </div>
    </div>
  );
}
